import classNames from "classnames";
import React, {
  ForwardedRef,
  forwardRef,
  TextareaHTMLAttributes,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
} from "react";
import { TextareaProps } from "./types";

const getConditionalProps = (
  invalid: TextareaProps["invalid"],
  counterID: string,
  disabled: TextareaProps["disabled"]
): TextareaHTMLAttributes<HTMLTextAreaElement> => ({
  "aria-invalid": invalid || undefined,
  "aria-describedby": counterID || undefined,
  disabled: disabled || undefined,
});

const counterId: string = `rds-textarea-counterID-${Math.random()
  .toString(36)
  .slice(2, 16)}`;

const calcHeight = (ref: HTMLTextAreaElement | null) => {
  if (!ref) return "auto";

  const borderWidth = Number(
    getComputedStyle(ref).getPropertyValue("border-width").split("px")[0]
  );
  const roundedBorderWidth = Math.round(borderWidth);

  return `${ref.scrollHeight + roundedBorderWidth * 2}`;
};

const buildStyles = (
  style: TextareaProps["style"],
  autoGrow: TextareaProps["autoGrow"],
  height: string
) => {
  const styleObj = {
    ...style,
  };
  if (autoGrow) {
    styleObj.resize = "none";
    styleObj.height = height;
  }
  return styleObj;
};

const Textarea = forwardRef(
  (
    {
      className,
      autoGrow = false,
      disabled = false,
      invalid = false,
      maxLength,
      size = "md",
      style = {},
      value,
      ...rest
    }: TextareaProps,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const textareaClasses = classNames(
      "rds-Textarea",
      {
        "is-disabled": disabled,
        "is-invalid": invalid,
        [`rds-Textarea--${size}`]: size !== "md",
      },
      className
    );

    // This allows us to maintain our internal ref to the textarea element and forward a ref passed in.
    const mergeRefs = (node: HTMLTextAreaElement) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref !== null) {
        ref.current = node;
      }

      textareaRef.current = node;
    };

    const [counter, setCounter] = useState(maxLength);
    const counterIdRef = useRef(counterId);

    useEffect(() => {
      if (maxLength && value !== undefined)
        setCounter(maxLength - value.length);
    }, []);

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [height, setHeight] = useState("auto");

    useLayoutEffect(() => {
      if (autoGrow && height === "auto") {
        setHeight(`${calcHeight(textareaRef.current)}px`);
      }
    }, [height, autoGrow]);

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const currentCharacterCount = e.target.value.length;
      if (maxLength) setCounter(maxLength - currentCharacterCount);

      if (autoGrow) setHeight("auto");

      if (rest.onChange) rest.onChange(e);
    };

    return (
      <div className={textareaClasses}>
        {!!maxLength && maxLength > 0 && (
          <span
            className="rds-Textarea-counter"
            aria-live="polite"
            id={counterIdRef.current}
          >
            {counter}
          </span>
        )}
        <textarea
          {...rest}
          {...getConditionalProps(invalid, counterIdRef.current, disabled)}
          ref={mergeRefs}
          onChange={onChange}
          maxLength={maxLength}
          value={value}
          style={buildStyles(style, autoGrow, height)}
        />
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;
